import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  TextField,
  Modal,
  MenuItem,
  Button,
} from "@mui/material";
import {
  ArrowDropUp,
  ArrowDropDown,
  UnfoldMore,
  Sync,
  ArrowBackIosNew,
  ArrowForwardIos,
  ArrowForward,
} from "@mui/icons-material";

const Attachments = () => {
  const [files, setFiles] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: null });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const fileInputRef = useRef(null);
  const [gotoPage, setGotoPage] = useState(1);
  const [selectedFile, setSelectedFile] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const totalPages = Math.max(1, Math.ceil(files.length / rowsPerPage));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [files, rowsPerPage]);

  useEffect(() => {
    setGotoPage(page);
  }, [page]);

  const handleView = (file) =>{
    setSelectedFile(file);
    setModalOpen(true);
  }
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const renderSortableHeader = (label, key) => {
    const isSorted = sortConfig.key === key;
    const direction = isSorted ? sortConfig.direction : null;
    return (
      <Box
        onClick={() => handleSort(key)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          fontWeight: "bold",
        }}
      >
        {label}
        {direction === "asc" && <ArrowDropUp />}
        {direction === "desc" && <ArrowDropDown />}
        {!direction && <UnfoldMore />}
      </Box>
    );
  };

  const handleFileUpload = (e) => {
    const uploadedFiles = Array.from(e.target.files || []);
    const mapped = uploadedFiles.map((file) => ({
      name: file.name,
      attachedBy: "You",
      date: new Date().toLocaleDateString(),
    //   file: file,
    raw:file,
    }));
    setFiles((prev) => [...prev, ...mapped]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files || []);
    const mapped = droppedFiles.map((file) => ({
      name: file.name,
      attachedBy: "You",
      date: new Date().toLocaleDateString(),
      file: file,
    }));
    setFiles((prev) => [...prev, ...mapped]);
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleViewImage = (file) => {
    if (file && file.type.startsWith("image/")) {
      const url = URL.createObjectURL(file);
      window.open(url, "_blank");
    } else {
      alert("This file is not viewable. Only image files can be previewed.");
    }
  };

  const paginatedFiles = files.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  return (
    <Box sx={{ p: 2 }}>
      {/* Upload */}
      <Box
        onClick={() => fileInputRef.current.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        sx={{
          border: "1px dashed #B5C4F1",
          borderRadius: "12px",
          p: 3,
          textAlign: "center",
          mb: 2,
          color: "#8c9eff",
          fontWeight: 500,
          cursor: "pointer",
        }}
      >
        <Typography>
          <span role="img" aria-label="upload" style={{ fontSize: "20px" }}>
            ☁️
          </span>{" "}
          Drop file here OR Click to upload file
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={handleFileUpload}
        />
      </Box>

      {/* Table */}
      <TableContainer sx={{ border: "1px solid black", borderRadius: 2, mb: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{borderBottom:"1px solid black"}}>{renderSortableHeader("File Name", "fileName")}</TableCell>
              <TableCell sx={{borderBottom:"1px solid black"}}>{renderSortableHeader("Attached By", "attachedBy")}</TableCell>
              <TableCell sx={{borderBottom:"1px solid black"}}>{renderSortableHeader("Uploaded Date", "uploadedDate")}</TableCell>
              <TableCell sx={{borderBottom:"1px solid black"}}><b>Action</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedFiles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} sx={{ textAlign: "center", py: 2 }}>
                  You do not have any uploaded files.
                </TableCell>
              </TableRow>
            ) : (
              paginatedFiles.map((file, index) => (
                <TableRow key={index}>
                  <TableCell>{file.name}</TableCell>
                  <TableCell>{file.attachedBy}</TableCell>
                  <TableCell>{file.date}</TableCell>
                  <TableCell>
                    {/* <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleViewImage(file.file)}
                    > */}
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleView(file)}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box
        sx={{
          border: "1px solid black",
          borderRadius: "8px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 16px",
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={() => setPage((prev) => Math.max(prev - 1, 1))}>
            <ArrowBackIosNew sx={{ fontSize: 12 }} />
          </IconButton>

          <Box
            sx={{
              border: "1px solid black",
              borderRadius: "50%",
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "bold",
            }}
          >
            {page}
          </Box>

          <IconButton onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}>
            <ArrowForwardIos sx={{ fontSize: 12 }} />
          </IconButton>

          <Button startIcon={<Sync />} sx={{ color: "#8c9eff", fontSize: "13px" }}>
            Displaying files {paginatedFiles.length}
          </Button>

          <Typography sx={{ fontSize: "13px", color: "#8c9eff" }}>
            Page {page} of {totalPages}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: "14px" }}>Show :</Typography>
            <TextField
              select
              size="small"
              value={rowsPerPage}
              onChange={(e) => {
                const newRows = Number(e.target.value);
                setRowsPerPage(newRows);
                setPage(1);
              }}
              sx={{ width: "fit-content" }}
            >
              {[10, 20, 30].map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Typography sx={{ fontSize: "14px" }}>Go to page</Typography>
          <TextField
            size="small"
            type="number"
            value={gotoPage}
            inputProps={{ min: 1, max: totalPages }}
            onChange={(e) => {
              let val = parseInt(e.target.value, 10);
              if (!isNaN(val)) {
                if (val < 1) val = 1;
                else if (val > totalPages) val = totalPages;
                setGotoPage(val);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && gotoPage >= 1 && gotoPage <= totalPages) {
                setPage(gotoPage);
              }
            }}
            sx={{ width: 60 }}
          />
          <IconButton
            sx={{ color: "#8c9eff" }}
            onClick={() => {
              if (gotoPage >= 1 && gotoPage <= totalPages) {
                setPage(gotoPage);
              }
            }}
          >
            <ArrowForward />
          </IconButton>
        </Box>
      </Box>
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
  <Box
    sx={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      bgcolor: "background.paper",
      borderRadius: 2,
      boxShadow: 24,
      p: 2,
      maxWidth: "90%",
      maxHeight: "90%",
      overflow: "auto",
    }}
  >
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
      <Typography variant="h6">{selectedFile?.name}</Typography>
      <Button
        onClick={() => setModalOpen(false)}
        size="small"
        variant="contained"
        color="error"
      >
        Close
      </Button>
    </Box>
    {selectedFile && (
      <Box sx={{ textAlign: "center" }}>
        <img
          src={URL.createObjectURL(new Blob([selectedFile.raw]))}
          alt={selectedFile.name}
          style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: "8px" }}
        />
      </Box>
    )}
  </Box>
</Modal>

    </Box>

    
  );
};

export default Attachments;
